/**
 * ip-bot-feedback SDK entry point.
 *
 * Usage (script tag):
 *   <script src="https://input.islandpitch.bot/sdk.js"
 *           data-project="NWP"
 *           data-jpd-project="PD"
 *           data-survey-id="your-posthog-survey-id"
 *           data-position="bottom-right"></script>
 *
 * Usage (JS API):
 *   IPFeedback.init({ project: 'NWP', jpdProject: 'PD', surveyId: '...' });
 *
 * Positioning override (host page CSS — pierces the shadow boundary):
 *   #ip-feedback-root { --ip-offset-bottom: 80px; }
 *   Available vars: --ip-offset-bottom, --ip-offset-top, --ip-offset-left, --ip-offset-right
 *   (default 20px each — useful when a fixed mobile bottom nav covers the trigger).
 */

import type { IPFeedbackConfig, ResolvedConfig } from "./types";
import { parseScriptAttributes, resolveConfig } from "./config";
import { createWidget, openWidget, destroyWidget } from "./ui/widget";
import { installConsoleInterceptor } from "./detection/console";
import { installNetworkInterceptor } from "./detection/network";

let currentConfig: ResolvedConfig | null = null;

// Cache script attributes at parse time — document.currentScript is only
// available during synchronous execution, and the async fallback should
// also only run once rather than on every init() call.
const cachedScriptAttrs = parseScriptAttributes();

function init(userConfig: Partial<IPFeedbackConfig> = {}): void {
  // Merge cached script attributes with user config (user config takes precedence)
  const merged = { ...cachedScriptAttrs, ...userConfig };
  const config = resolveConfig(merged);

  currentConfig = config;

  // Install interceptors
  if (config.captureConsoleErrors) {
    installConsoleInterceptor(config.maxConsoleErrors);
  }
  if (config.captureNetworkErrors) {
    installNetworkInterceptor(config.maxNetworkErrors);
  }

  // Create widget
  createWidget(config);
}

function open(): void {
  if (currentConfig) {
    openWidget(currentConfig);
  }
}

function destroy(): void {
  destroyWidget();
  currentConfig = null;
}

// Expose global API
window.IPFeedback = { init, open, destroy };

// Auto-init from script attributes if data-project is present
if (cachedScriptAttrs.project) {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init());
  } else {
    init();
  }
}
